// src/lib/api.js
import axios from "axios";

/* ============================================================
   CONFIG
============================================================ */

const API_BASE_URL = "https://gateway.megriva.com/qrmenu";
const FILE_BASE_URL = "https://statik.megriva.com/files/";

const client = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/* ============================================================
   REQUEST
============================================================ */

client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* ============================================================
   RESPONSE
============================================================ */

client.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem("refresh_token");

        if (!refreshToken) {
          throw new Error("Refresh token bulunamadı");
        }

        const refreshResponse = await client.get("/Auth/RefreshToken", {
          params: refreshToken ? { token: refreshToken } : {},
          withCredentials: true,
        });

        const newAccessToken = refreshResponse.data?.accessToken?.token;
        const newRefreshToken = refreshResponse.data?.refreshToken;

        if (!newAccessToken) {
          throw new Error("Yeni access token alınamadı");
        }

        localStorage.setItem("token", newAccessToken);

        if (newRefreshToken) {
          localStorage.setItem("refresh_token", newRefreshToken);
        }

        client.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return client(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");

        window.location.href = "/login";

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

/* ============================================================
   HELPERS
============================================================ */

const normalizeList = (res) => {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.items)) return res.data.items;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

export const getFileUrl = (pathOrUrl) => {
  if (!pathOrUrl) return "";
  if (typeof pathOrUrl !== "string") return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;

  return FILE_BASE_URL + pathOrUrl.replace(/^\/+/, "");
};

const DEFAULT_CATEGORY_ORDER = [
  "salata",
  "başlangıç",
  "baslangic",
  "corba",
  "çorba",
  "ana yemek",
  "anayemek",
  "yemek",
  "balık",
  "balik",
  "makarna",
  "tatlı",
  "tatli",
  "icecek",
  "içecek",
];

const normalizeText = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replaceAll("ı", "i")
    .replaceAll("ğ", "g")
    .replaceAll("ü", "u")
    .replaceAll("ş", "s")
    .replaceAll("ö", "o")
    .replaceAll("ç", "c");

const sortCategoriesByDefaultFlow = (list = []) =>
  [...list].sort((a, b) => {
    const aName = normalizeText(a.name);
    const bName = normalizeText(b.name);

    const aIndex = DEFAULT_CATEGORY_ORDER.findIndex((key) =>
      aName.includes(normalizeText(key))
    );

    const bIndex = DEFAULT_CATEGORY_ORDER.findIndex((key) =>
      bName.includes(normalizeText(key))
    );

    const safeA = aIndex === -1 ? 999 : aIndex;
    const safeB = bIndex === -1 ? 999 : bIndex;

    if (safeA !== safeB) return safeA - safeB;

    return Number(a.index ?? 0) - Number(b.index ?? 0);
  });

/* ============================================================
   MAPPERS
============================================================ */

const mapCategoryFromApi = (c) => ({
  id:
    c?.id != null
      ? Number(c.id)
      : c?.Id != null
      ? Number(c.Id)
      : c?.categoryId != null
      ? Number(c.categoryId)
      : c?.CategoryId != null
      ? Number(c.CategoryId)
      : null,

  name: c?.name ?? c?.Name ?? "",
  slug: c?.slug ?? c?.Slug ?? "",
  index: c?.index ?? c?.Index ?? 0,

  businessId:
    c?.businessId != null
      ? Number(c.businessId)
      : c?.BusinessId != null
      ? Number(c.BusinessId)
      : null,

  visible: c?.visible ?? c?.Visible ?? true,
});

const mapBusinessFromApi = (r) => ({
  id: r?.id ?? r?.Id ?? null,
  name: r?.name ?? r?.Name ?? "",
  address: r?.address ?? r?.Address ?? "",
  phone: r?.phone ?? r?.Phone ?? "",
  email: r?.email ?? r?.Email ?? "",
  subDomain: r?.subDomain ?? r?.SubDomain ?? "",

  imageId:
    r?.imageId ??
    r?.ImageId ??
    r?.imageFile?.id ??
    r?.ImageFile?.id ??
    null,

  typeConstantValue:
    r?.typeConstantValue ??
    r?.TypeConstantValue ??
    r?.businessTypeConstantValue ??
    r?.BusinessTypeConstantValue ??
    r?.businessSector ??
    r?.BusinessSector ??
    r?.businessType ??
    r?.BusinessType ??
    r?.typeConstant?.value ??
    r?.TypeConstant?.Value ??
    "",

  logo_url: getFileUrl(
    r?.logo_url ??
      r?.logoUrl ??
      r?.LogoUrl ??
      r?.imageFile?.fileUrl ??
      r?.imageFile?.details?.[0]?.fileUrl ??
      r?.ImageFile?.fileUrl ??
      r?.ImageFile?.details?.[0]?.fileUrl ??
      ""
  ),
});

const mapSubscriptionPlanFromApi = (p) => ({
  id: p?.id ?? p?.Id ?? null,
  name: p?.name ?? p?.Name ?? "",
  title: p?.title ?? p?.Title ?? "",
  description: p?.description ?? p?.Description ?? "",
  price: Number(p?.price ?? p?.Price ?? 0),
  currency: p?.currency ?? p?.Currency ?? "₺",
});

const mapProductFromApi = (p) => {
  const id =
    p?.id != null ? Number(p.id) : p?.Id != null ? Number(p.Id) : null;

  const category_id =
    p?.categoryId != null
      ? Number(p.categoryId)
      : p?.category_id != null
      ? Number(p.category_id)
      : p?.CategoryId != null
      ? Number(p.CategoryId)
      : null;

  const imageId =
    p?.imageId != null
      ? Number(p.imageId)
      : p?.ImageId != null
      ? Number(p.ImageId)
      : p?.imageFile?.id != null
      ? Number(p.imageFile.id)
      : p?.ImageFile?.id != null
      ? Number(p.ImageFile.id)
      : null;

  const fileUrl =
    p?.imageFile?.fileUrl ??
    p?.imageFile?.details?.[0]?.fileUrl ??
    p?.ImageFile?.fileUrl ??
    p?.ImageFile?.details?.[0]?.fileUrl ??
    "";

  const image_url = fileUrl
    ? getFileUrl(fileUrl)
    : p?.image_url ?? p?.imageUrl ?? "";

  const categoryIds = Array.isArray(p?.categoryIds)
    ? p.categoryIds.map(Number)
    : Array.isArray(p?.CategoryIds)
    ? p.CategoryIds.map(Number)
    : Array.isArray(p?.categories)
    ? p.categories
        .map((category) =>
          typeof category === "object"
            ? category.id ?? category.categoryId ?? category.Id
            : category
        )
        .filter(Boolean)
        .map(Number)
    : Array.isArray(p?.Categories)
    ? p.Categories.map((category) =>
        typeof category === "object"
          ? category.id ?? category.categoryId ?? category.Id
          : category
      )
        .filter(Boolean)
        .map(Number)
    : category_id
    ? [Number(category_id)]
    : [];

  const promotionIds = Array.isArray(p?.promotionIds)
    ? p.promotionIds.map(Number)
    : Array.isArray(p?.PromotionIds)
    ? p.PromotionIds.map(Number)
    : Array.isArray(p?.promotions)
    ? p.promotions
        .map((promotion) =>
          typeof promotion === "object"
            ? promotion.id ?? promotion.promotionId ?? promotion.Id
            : promotion
        )
        .filter(Boolean)
        .map(Number)
    : Array.isArray(p?.Promotions)
    ? p.Promotions.map((promotion) =>
        typeof promotion === "object"
          ? promotion.id ?? promotion.promotionId ?? promotion.Id
          : promotion
      )
        .filter(Boolean)
        .map(Number)
    : [];

  return {
    id,
    name: p?.name ?? p?.Name ?? "",
    description: p?.description ?? p?.Description ?? "",
    price: Number(p?.price ?? p?.Price ?? 0),
    discountedRate:
      p?.discountedRate ?? p?.DiscountedRate ?? p?.discounted_rate ?? 0,
    calories: p?.calories ?? p?.Calories ?? 0,
    ingredients: p?.ingredients ?? p?.Ingredients ?? "",
    status: p?.status ?? p?.Status ?? true,
    is_active: p?.status ?? p?.Status ?? true,

    category_id,
    categoryId: category_id,
    categoryIds,
    categories: p?.categories ?? p?.Categories ?? [],

    promotionIds,
    promotions: p?.promotions ?? p?.Promotions ?? [],

    imageId,
    image_url,
    imageFile: p?.imageFile ?? p?.ImageFile ?? null,
    slug: p?.slug ?? p?.Slug ?? "",
  };
};

/* ============================================================
   SUBSCRIPTION & PAYMENT
============================================================ */

async function getSubscriptions(page = 0, pageSize = 1000) {
  const res = await client.get("/Subscription/GetList", {
    params: {
      Page: page,
      PageSize: pageSize,
    },
  });

  return res.data;
}

async function getSubscriptionPlans(page = 0, pageSize = 10) {
  const res = await client.get("/SubscriptionPlan/GetList", {
    params: {
      Page: page,
      PageSize: pageSize,
    },
  });

  const rawList = Array.isArray(res?.data?.items) ? res.data.items : [];
  const items = rawList.map(mapSubscriptionPlanFromApi);

  const base =
    typeof res.data === "object" && !Array.isArray(res.data) ? res.data : {};

  return {
    ...base,
    items,
  };
}

async function createSubscriptionPayment(payload) {
  const res = await client.post("/SubscriptionPayment/Payment", payload);
  return res.data;
}

async function createSubscriptionPayment3d(payload) {
  const res = await client.post("/SubscriptionPayment/Payment3d", payload);
  return res.data;
}

function normalizeSubscriptionFromApi(subscription) {
  return {
    id: subscription?.id ?? subscription?.Id ?? null,
    businessId: subscription?.businessId ?? subscription?.BusinessId ?? null,
    planId: subscription?.planId ?? subscription?.PlanId ?? null,
    startDate: subscription?.startDate ?? subscription?.StartDate ?? null,
    endDate: subscription?.endDate ?? subscription?.EndDate ?? null,
    status: subscription?.status ?? subscription?.Status ?? null,
    autoRenew: subscription?.autoRenew ?? subscription?.AutoRenew ?? false,
    createdDate: subscription?.createdDate ?? subscription?.CreatedDate ?? null,
  };
}

async function getCurrentSubscription() {
  const data = await getSubscriptions(0, 1000);

  const items = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data?.data?.items)
    ? data.data.items
    : Array.isArray(data)
    ? data
    : [];

  const normalizedItems = items.map(normalizeSubscriptionFromApi);

  const now = new Date();

  const activeSubscription =
    normalizedItems
      .filter((subscription) => {
        const status = Number(subscription.status);
        const endDate = subscription.endDate
          ? new Date(subscription.endDate)
          : null;

        return status === 1 && endDate && endDate >= now;
      })
      .sort((a, b) => new Date(b.endDate) - new Date(a.endDate))[0] || null;

  return {
    current: activeSubscription,
    items: normalizedItems,
  };
}

/* ============================================================
   API OBJECT
============================================================ */

export const api = {
  getSubscriptions,
  getCurrentSubscription,
  getSubscriptionPlans,
  createSubscriptionPayment,
  createSubscriptionPayment3d,

  /* =========================
     AUTH
  ========================= */

  addUser: ({ email, firstName, lastName, password, phone }) =>
    client.post("/Auth/Add", {
      email,
      firstName,
      lastName,
      password,
      phone,
    }),

  register: ({
    email,
    firstName,
    lastName,
    password,
    phone,
    userPhone,
    businessName,
    businessAddress,
    businessPhone,
    businessEmail,
    businessSubDomain,
    businessSector,
  }) =>
    client.post("/Auth/Register", {
      email: (email ?? "").trim().toLowerCase(),
      firstName: firstName?.trim() || "",
      lastName: lastName?.trim() || "",
      password,
      phone: (phone ?? userPhone ?? "").trim(),
      business: {
        name: businessName?.trim() || "",
        address: businessAddress?.trim() || "",
        phone: businessPhone?.trim() || "",
        email: businessEmail?.trim() || "",
        subDomain: businessSubDomain?.trim().toLowerCase() || "",
        typeConstantValue: businessSector || "",
        imageId: 0,
      },
    }),

  login: ({ email, password }) =>
    client.post("/Auth/Login", {
      email: (email ?? "").trim().toLowerCase(),
      password,
    }),

  changePassword: ({ newPassword }) =>
    client.put("/Auth/ChangePassword", {
      newPassword,
    }),

  forgotPassword: ({ email }) =>
    client.post("/Auth/ForgotPassword", {
      email: (email ?? "").trim().toLowerCase(),
    }),

  validateOtp: ({ otp }) =>
    client.post("/Auth/ValidateOtp", {
      otp: String(otp ?? "").trim(),
    }),

  changeForgotPassword: ({ newPassword, validateToken }) =>
    client.put("/Auth/ChangePassword", {
      newPassword,
      validateToken,
    }),

  refreshToken: (token) =>
    client.get("/Auth/RefreshToken", {
      params: token ? { token } : {},
    }),

  getAuthDetail: () => client.get("/Auth/Detail"),

  revokeToken: (value) => client.put("/Auth/RevokeToken", value ?? ""),

  deleteAccount: (value) =>
    client.delete("/Auth/Delete", {
      data: value ?? "",
    }),

/* =========================
   TICKET
========================= */

addTicket: (payload) =>
  client.post("/Ticket/Add", payload).then((res) => res.data),

  getTickets: ({ page = 0, pageSize = 1000 } = {}) =>
    client
      .get("/Ticket/GetList", {
        params: {
          "PageRequest.Page": page,
          "PageRequest.PageSize": pageSize,
        },
      })
      .then((res) => res.data),

deleteTicket: (id) => {
  const ticketId = Number(id);

  if (
    !Number.isInteger(ticketId) ||
    ticketId <= 0 ||
    ticketId > 2147483647
  ) {
    return Promise.reject(
      new Error("Geçerli backend ticket id bulunamadı.")
    );
  }

  return client
    .delete("/Ticket/Delete", {
      data: {
        id: ticketId,
      },
    })
    .then((res) => res.data);
},

  /* =========================
     ADDRESS
  ========================= */

  addAddress: (payload) => client.post("/Address/Add", payload),

  getAddresses: (params) =>
    client.get("/Address/GetList", {
      params,
    }),

  getAddressById: (id) => client.get(`/Address/GetById/${id}`),

  deleteAddress: (id) =>
    client.delete("/Address/Delete", {
      data: { id },
    }),

  /* =========================
     CONSTANT
  ========================= */

  getConstants: ({ name, value, type, page = 0, pageSize = 100 } = {}) =>
    client
      .get("/Constant/GetList", {
        params: {
          Name: name || undefined,
          Value: value || undefined,
          Type: type || undefined,
          "PageRequest.Page": page,
          "PageRequest.PageSize": pageSize,
        },
      })
      .then((res) => res.data),

  getPublicConstants: ({
    name,
    value,
    type,
    page = 0,
    pageSize = 1000,
  } = {}) =>
    client
      .get("/Constant/GetListPublic", {
        params: {
          Name: name || undefined,
          Value: value || undefined,
          Type: type || undefined,
          "PageRequest.Page": page,
          "PageRequest.PageSize": pageSize,
        },
      })
      .then((res) => res.data),

  /* =========================
     CATEGORY
  ========================= */

  createCategory: ({ name, index, businessId, visible }) =>
    client.post("/Category/Add", {
      name,
      index: index ?? 0,
      businessId: businessId ?? 0,
      visible: visible ?? true,
    }),

  getCategories: ({
    businessSubDomain,
    visible,
    page = 0,
    pageSize = 1000,
  } = {}) =>
    client
      .get("/Category/GetList", {
        params: {
          BusinessSubDomain: businessSubDomain,
          Visible: visible,
          "PageRequest.Page": page,
          "PageRequest.PageSize": pageSize,
        },
      })
      .then((res) => {
        const list = normalizeList(res).map(mapCategoryFromApi);
        const sortedList = sortCategoriesByDefaultFlow(list);

        return {
          ...res,
          data: sortedList,
        };
      }),

  getPublicCategories: ({
    businessSubDomain,
    page = 0,
    pageSize = 1000,
  } = {}) =>
    client
      .get("/Category/GetListPublic", {
        params: {
          BusinessSubDomain: businessSubDomain,
          "PageRequest.Page": page,
          "PageRequest.PageSize": pageSize,
        },
      })
      .then((res) => {
        const list = normalizeList(res).map(mapCategoryFromApi);
        const sortedList = sortCategoriesByDefaultFlow(list);

        return {
          ...res,
          data: sortedList,
        };
      }),

  getCategoryById: (id) =>
    client.get(`/Category/GetById/${id}`).then((res) => ({
      ...res,
      data: mapCategoryFromApi(res.data),
    })),

  updateCategory: ({ id, name, index, slug }) =>
    client.put("/Category/Update", {
      id,
      name,
      index: index ?? 0,
      slug: slug ?? "",
    }),

  changeCategoryVisible: (id, visible) =>
    client.put("/Category/ChangeVisible", {
      id,
      visible,
    }),

  deleteCategory: (id) =>
    client.delete("/Category/Delete", {
      data: { id },
    }),

  addProductsToCategory: ({ id, productIds }) =>
    client.post("/Category/AddProducts", {
      id: Number(id),
      productIds: productIds.map(Number),
    }),

  deleteProductsFromCategory: ({ id, productIds }) =>
    client.delete("/Category/DeleteProducts", {
      data: {
        id: Number(id),
        productIds: productIds.map(Number),
      },
    }),

  reorderCategoryProducts: ({ categoryId, orderedProductIds }) =>
    client.put("/Category/ReorderProducts", {
      categoryId: Number(categoryId),
      orderedProductIds: orderedProductIds.map(Number),
    }),

  /* =========================
     PRODUCT
  ========================= */

  getProducts: ({
    page = 0,
    pageSize = 1000,
    status,
    categorySlug,
    promotionId,
    promotionSlug,
    businessSubDomain,
  } = {}) =>
    client
      .get("/Product/GetList", {
        params: {
          ...(businessSubDomain
            ? { BusinessSubDomain: businessSubDomain }
            : {}),
          ...(categorySlug ? { CategorySlug: categorySlug } : {}),
          ...(promotionId != null
            ? { PromotionId: promotionId, promotionId }
            : {}),
          ...(promotionSlug ? { PromotionSlug: promotionSlug } : {}),
          ...(typeof status === "boolean" ? { Status: status, status } : {}),
          "PageRequest.Page": page,
          "PageRequest.PageSize": pageSize,
        },
      })
      .then((res) => {
        const list = normalizeList(res).map(mapProductFromApi);

        return {
          ...res,
          data: list,
        };
      }),

  getPublicProducts: ({
    businessSubDomain,
    categorySlug,
    promotionSlug,
    page = 0,
    pageSize = 1000,
  } = {}) =>
    client
      .get("/Product/GetListPublic", {
        params: {
          BusinessSubdomain: businessSubDomain,
          ...(categorySlug ? { CategorySlug: categorySlug } : {}),
          ...(promotionSlug ? { PromotionSlug: promotionSlug } : {}),
          "PageRequest.Page": page,
          "PageRequest.PageSize": pageSize,
        },
      })
      .then((res) => ({
        ...res,
        data: normalizeList(res).map(mapProductFromApi),
      })),

  createProduct: ({
    name,
    description,
    price,
    discountedRate,
    calories,
    ingredients,
    imageId,
    status,
  }) =>
    client.post("/Product/Add", {
      name,
      description: description ?? "",
      price: Number(price ?? 0),
      discountedRate: discountedRate ?? 0,
      calories: calories ?? 0,
      ingredients: ingredients ?? "",
      imageId: imageId ? Number(imageId) : null,
      status: status ?? true,
    }),

  getProductById: (id) =>
    client.get(`/Product/GetById/${id}`).then((res) => ({
      ...res,
      data: mapProductFromApi(res.data),
    })),

  updateProduct: ({
    id,
    name,
    price,
    description,
    imageId,
    discountedRate,
    calories,
    ingredients,
  }) =>
    client.put("/Product/Update", {
      id: Number(id),
      name: name ?? "",
      price: Number(price ?? 0),
      discountedRate: Number(discountedRate ?? 0),
      calories: Number(calories ?? 0),
      ingredients: ingredients ?? "",
      imageId: imageId ? Number(imageId) : null,
      description: description ?? "",
    }),

  changeProductStatus: (id, status) =>
    client.put("/Product/ChangeStatus", {
      id,
      status,
    }),

  deleteProduct: (id) =>
    client.delete("/Product/Delete", {
      data: { id },
    }),

  getMenuItems: (options) => {
    if (typeof options === "number") {
      return api.getProducts({
        categoryId: options,
      });
    }

    return api.getProducts(options);
  },

  getPublicMenuItems: (options) => api.getPublicProducts(options),

  createMenuItem: (data) =>
    api.createProduct({
      name: data.name,
      description: data.description,
      price: data.price,
      discountedRate: data.discountedRate ?? 0,
      calories: data.calories ?? 0,
      ingredients: data.ingredients ?? "",
      imageId: data.imageId ? Number(data.imageId) : null,
      status: data.status ?? data.is_active ?? true,
    }),

  updateMenuItem: (id, data) =>
    api.updateProduct({
      id,
      name: data.name,
      description: data.description,
      price: data.price,
      discountedRate: data.discountedRate ?? 0,
      calories: data.calories ?? 0,
      ingredients: data.ingredients ?? "",
      imageId: data.imageId ? Number(data.imageId) : null,
    }),

  deleteMenuItem: (id) => api.deleteProduct(id),

  /* =========================
     PROMOTION
  ========================= */

  getPromotions: ({ visible, page = 0, pageSize = 1000 } = {}) =>
    client
      .get("/Promotion/GetList", {
        params: {
          ...(typeof visible === "boolean" ? { visible } : {}),
          "PageRequest.Page": page,
          "PageRequest.PageSize": pageSize,
        },
      })
      .then((res) => ({
        ...res,
        data: normalizeList(res),
      })),

  getPublicPromotions: ({
    businessSubDomain,
    page = 0,
    pageSize = 1000,
  } = {}) =>
    client
      .get("/Promotion/GetListPublic", {
        params: {
          BusinessSubDomain: businessSubDomain,
          "PageRequest.Page": page,
          "PageRequest.PageSize": pageSize,
        },
      })
      .then((res) => ({
        ...res,
        data: normalizeList(res),
      })),

  createPromotion: ({
    name,
    description,
    imageId,
    visible,
    beginDate,
    endDate,
  }) =>
    client.post("/Promotion/Add", {
      name,
      description: description ?? "",
      imageId: imageId ?? null,
      visible: visible ?? true,
      beginDate,
      endDate,
    }),

  updatePromotion: ({
    id,
    name,
    description,
    imageId,
    visible,
    beginDate,
    endDate,
  }) =>
    client.put("/Promotion/Update", {
      id,
      name,
      description: description ?? "",
      imageId: imageId ?? null,
      visible: visible ?? true,
      beginDate,
      endDate,
    }),

  changePromotionVisible: (id, visible) =>
    client.put("/Promotion/ChangeVisible", {
      id,
      visible,
    }),

  deletePromotion: (id) =>
    client.delete("/Promotion/Delete", {
      data: { id },
    }),

  reorderPromotions: (orderedPromotionIds = []) =>
    client.put("/Promotion/Reorder", {
      orderedPromotionIds: orderedPromotionIds.map(Number),
    }),

  addProductsToPromotion: ({ id, productIds }) =>
    client.post("/Promotion/AddProducts", {
      id: Number(id),
      productIds: productIds.map(Number),
    }),

  deleteProductsFromPromotion: ({ id, productIds }) =>
    client.delete("/Promotion/DeleteProducts", {
      data: {
        id: Number(id),
        productIds: productIds.map(Number),
      },
    }),

  reorderPromotionProducts: ({ promotionId, orderedProductIds }) =>
    client.put("/Promotion/ReorderProducts", {
      promotionId: Number(promotionId),
      orderedProductIds: orderedProductIds.map(Number),
    }),

  /* =========================
     BUSINESS
  ========================= */

  getBusinessById: (id) =>
    client.get(`/Business/GetById/${id}`).then((res) => ({
      ...res,
      data: mapBusinessFromApi(res.data),
    })),

  getBusinessBySubDomain: (subDomain) =>
    client
      .get(`/Business/GetBySubDomain/${encodeURIComponent(subDomain)}`)
      .then((res) => ({
        ...res,
        data: mapBusinessFromApi(res.data),
      })),

  updateBusiness: ({
  id,
  name,
  address,
  phone,
  email,
  imageId,
  TypeConstantValue,
  typeConstantValue,
  businessSector,
}) => {
  const selectedTypeConstantValue =
    TypeConstantValue || typeConstantValue || businessSector || "";

  return client.put("/Business/Update", {
    id,
    name,
    address,
    phone,
    email,
    ...(imageId !== undefined && imageId !== null ? { imageId } : {}),
    TypeConstantValue: selectedTypeConstantValue,
  });
},

  getPublicMenu: async (slug) => {
    const safeSlug = String(slug || "").trim();

    if (!safeSlug) {
      throw new Error("Slug bulunamadı");
    }

    const businessRes = await client.get(
      `/Business/GetBySubDomain/${encodeURIComponent(safeSlug)}`
    );

    const businessRaw = businessRes?.data || {};
    const business = mapBusinessFromApi(businessRaw);

    if (!business?.id) {
      throw new Error("Restoran bulunamadı");
    }

    const categoryRes = await client.get("/Category/GetListPublic", {
      params: {
        BusinessSubDomain: safeSlug,
        "PageRequest.Page": 0,
        "PageRequest.PageSize": 1000,
      },
    });

    const categories = sortCategoriesByDefaultFlow(
      normalizeList(categoryRes).map(mapCategoryFromApi)
    );

    const productRes = await client.get("/Product/GetListPublic", {
      params: {
        BusinessSubDomain: safeSlug,
        Status: true,
        status: true,
        "PageRequest.Page": 0,
        "PageRequest.PageSize": 1000,
      },
    });

    const products = normalizeList(productRes).map(mapProductFromApi);

    const categoriesWithItems = categories.map((category) => ({
      ...category,
      items: products.filter((product) =>
        Array.isArray(product.categoryIds)
          ? product.categoryIds.map(String).includes(String(category.id))
          : String(product.categoryId) === String(category.id)
      ),
    }));

    return {
      data: {
        business: {
          id: business.id,
          name: business.name,
          logo_url: business.logo_url || "",
          contact_phone: business.phone || "",
          contact_address: business.address || "",
        },
        categories: categoriesWithItems,
        products,
      },
    };
  },

  /* =========================
     POPUP
  ========================= */

  getPopupList: (params = {}) =>
    client.get("/Popup/GetList", {
      params,
    }),

  getPublicPopup: (businessSubDomain) =>
    client.get("/Popup/GetPublic", {
      params: {
        BusinessSubDomain: businessSubDomain,
      },
    }),

  addPopup: (payload) =>
    client.post("/Popup/Add", payload),

  updatePopup: (payload) =>
    client.put("/Popup/Update", payload),

  /* =========================
     BUSINESS REGISTERED CARD
  ========================= */

  addBusinessRegisteredCard: (payload) =>
    client.post("/BusinessRegisteredCard/Add", payload),

  getBusinessRegisteredCards: (params) =>
    client.get("/BusinessRegisteredCard/GetList", {
      params,
    }),

  getBusinessRegisteredCardById: (id) =>
    client.get(`/BusinessRegisteredCard/GetById/${id}`),

  deleteBusinessRegisteredCard: (id) =>
    client.delete("/BusinessRegisteredCard/Delete", {
      data: { id },
    }),

  /* =========================
     FILES
  ========================= */

  uploadFile: (formData) =>
    client.post("/Files/Add", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),

  uploadFileBase64: (payload) => client.post("/Files/AddBase64", payload),

  getFileById: (id) => client.get(`/Files/${id}`),

  getFiles: (params) =>
    client.get("/Files/GetList", {
      params,
    }),

  deleteFile: (id) =>
    client.delete("/Files", {
      data: { id },
    }),
};

/* ============================================================
   FILE HELPERS
============================================================ */

export const uploadQrMenuImage = async (file) => {
  const formData = new FormData();

  formData.append("ProjectName", "qrmenu");
  formData.append("DirectoryName", "products");
  formData.append("File", file);

  const res = await client.post("/Files/Add", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  const data = res.data ?? res;

  const id = data?.id ?? data?.fileId ?? data?.ImageId ?? data?.imageId ?? null;
  const fileUrl = data?.fileUrl ?? data?.details?.[0]?.fileUrl ?? "";

  return {
    id,
    fileUrl,
    url: getFileUrl(fileUrl),
    raw: data,
  };
};

export const deleteQrMenuImage = async (fileId) => {
  const res = await client.delete("/Files", {
    data: {
      id: fileId,
    },
  });

  return res.data;
};

export default api;